import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from "@mui/material";
import { useRevalidator } from "react-router";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { faCalendarDays } from "@fortawesome/pro-solid-svg-icons";
import dayjs from "dayjs";

import { FormErrorList, useFormAction } from "../../form";
import { useClientOptions } from "./hooks/useClientOptions";
import { useLocationOptions } from "./hooks/useLocationOptions";
import { status } from "shared/src/appointment.mjs";
import { useDoctorOptions } from "./hooks/useDoctorOptions";
import * as api from "../../services/api";
import * as d from "shared/src/date.mjs";
import ButtonLoader from "../ButtonLoader";
import { ManualDateSelectionDialog } from "./ManualDateSelectionDialog";
import { ClientReservation } from "./ClientReservation";
import * as doc from "./doctor.mjs";
import { useSnackbarMessage } from "../Snackbar.jsx";

function doctorData(client) {
  const selectedDoctors = client?.selectedDoctors;
  const doctors = selectedDoctors?.doctors ?? [];

  if (selectedDoctors?.any) {
    return { any_doctor: true };
  }

  if (doctors.length === 0) {
    return {};
  }

  return { doctor_ids: doctors.map((d) => d.id) };
}

export const RESERVATION_MODES = {
  NEW: "new",
  RESCHEDULE: "reschedule",
  FOLLOWUP: "followup",
};

const initialClientState = (defaults) => {
  return {
    client: defaults.client ?? null,
    serviceOption: null,
    locationOption: defaults.locationOption ?? null,
    appointment: defaults.appointment ?? null,
    oldAppointment: defaults.oldAppointment ?? null,
    note: defaults.note ?? null,
  };
};

const initialReservationSettingState = (defaults) => {
  return {
    mode: defaults.mode ?? RESERVATION_MODES.NEW,
    order_from_h: defaults.order_from_h ?? 5,
    selectedWeekdays: [false, false, false, false, false],
    selectedTimeParts: [false, false, false, false, false],
    customTimeFrom: "06:00",
    customTimeTo: "23:00",
    calendarRange: [dayjs(), dayjs().add(7, "day").hour(23)],
    isDateManuallySelected: false,
    showManual: false,
    isJointConsultation: false,
    anyClientHasNote: false,

    suggestions: [],
    suggestionsLoading: false,
    suggestion: null,
  };
};

export const ReservationDialog = ({ close, defaults = {} }) => {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const displaySnackbarMessage = useSnackbarMessage();

  // Normalize defaults by deriving missing values from the provided appointment
  /**
   * @typedef {Object} DialogDefaults
   * @property {any=} appointment
   * @property {any=} oldAppointment
   * @property {any=} client
   * @property {any=} locationOption
   * @property {any=} serviceOption
   * @property {any=} productOption
   * @property {any=} doctorOption
   * @property {string=} mode
   */
  /** @type {DialogDefaults} */
  const normalizedDefaults = useMemo(() => {
    /** @type {DialogDefaults} */
    const base = { ...(defaults || {}) };
    const appt = base.appointment ?? base.oldAppointment ?? null;
    if (appt) {
      base.client = base.client ?? appt.client ?? null;
      base.locationOption =
        base.locationOption ?? appt.location ?? appt.calendar?.location ?? null;
    }
    return base;
  }, [defaults]);

  const [clientState, setClientState] = useState(() =>
    initialClientState(normalizedDefaults),
  );

  const [reservationSettingState, setReservationSettingState] = useState(() =>
    initialReservationSettingState(defaults),
  );

  const [selectedClients, setSelectedClients] = useState([
    {
      selectedClient: null,
      selectedLocation: null,
      selectedProduct: null,
      selectedService: null,
      selectedDoctors: doc.selectDoctors([]),
      selectedSuggestion: null,
      id: crypto.randomUUID(),
      note: null,
    },
  ]);

  useEffect(() => {
    const hasDifferentService = selectedClients.some(
      (client) =>
        client.selectedService?.id !== "dad9a0a6-e0f2-429d-ae14-d1f6afdea397",
    );

    if (hasDifferentService) {
      setReservationSettingState((setting) => ({
        ...setting,
        isJointConsultation: false,
      }));
    }
  }, [selectedClients, setReservationSettingState]);

  const {
    client,
    appointment,
    oldAppointment,
    locationOption,
    productOption,
    serviceOption,
    doctorOption,
  } = clientState;

  const { mode, showManual, prevState } = reservationSettingState;

  // Seed current appointment for the primary panel when available
  useEffect(() => {
    if (!appointment) return;
    setSelectedClients((prev) => {
      if (!prev.length) return prev;
      const next = [...prev];
      next[0] = { ...next[0], currentAppointment: appointment };
      return next;
    });
  }, [appointment]);

  // Shared option lists (same across all panels); location is controlled by primary
  const clientOptions = useClientOptions();
  const locationOptions = useLocationOptions();
  const doctorOptions = useDoctorOptions(
    selectedClients[0]?.selectedLocation?.id ?? locationOption?.id,
  );

  // SUBMIT FORM
  const { errors, loading, onSubmit } = useFormAction(
    async (_) => {
      const uuid = crypto.randomUUID();
      switch (mode) {
        case RESERVATION_MODES.NEW:
          if (reservationSettingState.isJointConsultation) {
            return api.createAppointment({
              calendar_id: selectedClients[0].selectedSuggestion.calendar_id,
              client_ids: selectedClients.map(
                (client) => client.selectedClient.id,
              ),
              service_id: selectedClients[0].selectedService.id,
              start: d.toString(
                new Date(selectedClients[0].selectedSuggestion.start),
              ),
              end: d.toString(
                new Date(selectedClients[0].selectedSuggestion.end),
              ),
              group_id: uuid,
              ...doctorData(selectedClients[0]),
              note: selectedClients[0].note ?? null,
            });
          } else {
            const follows =
              reservationSettingState?.note == null
                ? null
                : reservationSettingState?.oldAppointment?.id;
            const appointments = selectedClients.map((client) => ({
              calendar_id: client.selectedSuggestion.calendar_id,
              client_ids: [client.selectedClient.id],
              service_id: client.selectedService.id,
              start: d.toString(new Date(client.selectedSuggestion.start)),
              end: d.toString(new Date(client.selectedSuggestion.end)),
              reservation_id: selectedClients.length > 1 ? uuid : null,
              note: client.selectedClient.note ?? null,
              follows,
              ...doctorData(client),
            }));

            return api.createAppointments(appointments);
          }

        case RESERVATION_MODES.RESCHEDULE:
          if (reservationSettingState.isJointConsultation) {
            return api.createAppointment({
              calendar_id: selectedClients[0].selectedSuggestion.calendar_id,
              client_ids: selectedClients.map(
                (client) => client.selectedClient.id,
              ),
              service_id: selectedClients[0].selectedService.id,
              start: d.toString(
                new Date(selectedClients[0].selectedSuggestion.start),
              ),
              end: d.toString(
                new Date(selectedClients[0].selectedSuggestion.end),
              ),
              group_id: uuid,
              rescheduled_from_id: appointment.id,
              replaces: selectedClients.map(
                (client) => client.currentAppointment.id,
              ),
              note: selectedClients[0].note ?? null,
              ...doctorData(selectedClients[0]),
            });
          } else {
            const appointments = selectedClients.map((client) => ({
              calendar_id: client.selectedSuggestion.calendar_id,
              client_ids: [client.selectedClient.id],
              service_id: client.selectedService.id,
              start: d.toString(new Date(client.selectedSuggestion.start)),
              end: d.toString(new Date(client.selectedSuggestion.end)),
              reservation_id: selectedClients.length > 1 ? uuid : null,
              rescheduled_from_id: appointment.id,
              replaces: [client.currentAppointment.id],
              note: client.selectedClient.note ?? null,
              ...doctorData(client),
            }));

            return api.createAppointments(appointments);
          }

        case RESERVATION_MODES.FOLLOWUP: {
          // Use the primary panel's selections for follow-up
          const primary = selectedClients[0];
          const sel = primary?.selectedSuggestion;
          if (!sel) {
            throw new Error("No suggestion selected for follow-up");
          }

          return api.createAppointment({
            calendar_id: sel.calendar_id,
            client_ids: [primary?.selectedClient?.id ?? client?.id],
            service_id: primary?.selectedService?.id ?? serviceOption.id,
            start: d.toString(new Date(sel.start)),
            end: d.toString(new Date(sel.end)),
            follows: oldAppointment.id,
            note: primary?.selectedClient?.note ?? null,
            ...doctorData(primary),
          });
        }

        default:
          console.error("Neznámý typ rezervace");
          displaySnackbarMessage({
            severity: "error",
            title: "Neznámý typ rezervace. Prosím zkuste akci opakovat.",
            content: "",
          });
      }
    },
    () => {
      revalidator.revalidate();
      displaySnackbarMessage({
        title:
          mode === RESERVATION_MODES.RESCHEDULE
            ? "Rezervace byla úspěšně změněna."
            : "Rezervace byla úspěšně vytvořena.",
        content: "",
      });
      close();
    },
  );

  const defaultOptions = useMemo(
    () =>
      selectedClients.map((sc, idx) => ({
        client: idx === 0 ? (client ?? sc.selectedClient) : sc.selectedClient,
        product:
          idx === 0
            ? (sc.selectedProduct ?? productOption)
            : sc.selectedProduct,
        service:
          idx === 0
            ? (sc.selectedService ?? serviceOption)
            : sc.selectedService,
        // Primary uses its own/selected location, others mirror primary's selection
        location:
          idx === 0
            ? (sc.selectedLocation ?? locationOption)
            : (selectedClients[0]?.selectedLocation ?? locationOption),
      })),
    [
      client,
      productOption,
      serviceOption,
      doctorOption,
      selectedClients,
      locationOption,
    ],
  );

  useEffect(() => {
    const { group_id, reservation_id } = clientState.appointment || {};

    const getGroupAppointments = async () => {
      const { data } = await api.loadGroupAppointments({
        filter: {
          and: [
            {
              status_id: {
                in: [status.BOOKED, status.CONFIRMED, status.WAITING],
              },
            },
            {
              or: [
                { group_id: { eq: group_id } },
                { reservation_id: { eq: reservation_id } },
              ],
            },
          ],
        },
      });
      const groupAppointments = data.appointments.data;

      // Prefill additional panels for other top-level appointments in the group
      // Only extend if we haven't already added extra clients
      setSelectedClients((prev) => {
        if (
          !Array.isArray(groupAppointments) ||
          groupAppointments.length === 0
        ) {
          return prev;
        }

        // Keep existing if user already added more panels
        if (prev.length > 1) {
          return prev;
        }

        const primaryApptId = appointment?.id ?? clientState.appointment?.id;
        const primaryClientId = client?.id ?? appointment?.client?.id;

        // Consider only top-level (no parent_id) and exclude current appointment
        const topLevel = groupAppointments.filter(
          (a) => !a.parent_id && a.id !== primaryApptId,
        );

        // De-duplicate by client id and cap total panels to 4
        const seenClientIds = new Set();
        if (primaryClientId) seenClientIds.add(primaryClientId);

        const toAdd = [];
        for (const appt of topLevel) {
          const cid = appt?.client?.id;
          if (!cid || seenClientIds.has(cid)) continue;
          toAdd.push({
            selectedClient: appt.client ?? null,
            // Non-primary panels rely on defaultOptions[0].location (mirrored from primary)
            selectedLocation: null,
            // Let ClientReservation resolve product when options load
            selectedProduct: null,
            selectedService: null,
            // Doctor is resolved inside ClientReservation when options load
            selectedDoctors: doc.selectDoctors([]),
            selectedSuggestion: null,
            id: crypto.randomUUID(),
            note: null,
            currentAppointment: appt,
          });
          if (prev.length + toAdd.length >= 4) break; // cap at 4 panels total
        }

        if (toAdd.length === 0) return prev;
        return [prev[0], ...toAdd];
      });
    };
    if (clientState.appointment && (group_id || reservation_id)) {
      getGroupAppointments();
    }
  }, [clientState.appointment]);

  const submitDisabled =
    !(
      selectedClients.filter((client) => client.selectedSuggestion).length ===
      selectedClients.length
    ) &&
    !(
      selectedClients[0].selectedSuggestion != null &&
      reservationSettingState.isJointConsultation
    );

  const dialogWidth = useMemo(() => {
    let width = "md";
    switch (selectedClients.length) {
      case 1:
        width = reservationSettingState.anyClientHasNote ? "lg" : "md";
        break;
      case 2:
        width = "md";
        break;
      case 3:
        width = "lg";
        break;
      default:
        width = "xl";
    }
    return width;
  }, [selectedClients, reservationSettingState.anyClientHasNote]);

  // Stable callback to receive primary suggestions from first client panel
  const handlePrimarySuggestionsUpdate = useCallback(
    (suggestions, loading) => {
      setClientState((prev) => {
        const prevSuggestions = prev.suggestions;
        let suggestionsChanged = prevSuggestions !== suggestions;
        if (!suggestionsChanged && prevSuggestions && suggestions) {
          if (
            prevSuggestions.length !== suggestions.length ||
            !prevSuggestions.every((s, i) => s.id === suggestions[i]?.id)
          ) {
            suggestionsChanged = true;
          }
        }
        const loadingChanged = prev.suggestionsLoading !== loading;
        if (!suggestionsChanged && !loadingChanged) return prev;
        if (!suggestionsChanged) {
          return { ...prev, suggestionsLoading: loading };
        }
        return { ...prev, suggestions, suggestionsLoading: loading };
      });
      // Reset shared page index when underlying suggestions change
      setReservationSettingState((state) => ({
        ...state,
        primarySuggestionsPage: 0,
      }));
    },
    [setReservationSettingState],
  );

  // Allow replacing a manually selected suggestion with a standard one
  const clearManualSelection = useCallback(() => {
    if (reservationSettingState.isDateManuallySelected) {
      setReservationSettingState((prev) => ({
        ...prev,
        isDateManuallySelected: false,
      }));
      setClientState((prev) => ({
        ...prev,
        suggestion: null,
      }));
    }
  }, []);

  return (
    <Dialog
      open={true}
      onClose={() => close()}
      maxWidth={dialogWidth}
      scroll="paper"
    >
      <DialogTitle>
        {appointment
          ? t("calendar.rescheduleReservation")
          : t("calendar.newReservation")}
        <IconButton aria-label="close" onClick={() => close()}>
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={onSubmit} id="reservationForm">
          <Box>
            <Grid container spacing={1}>
              {selectedClients.map((client, index) => (
                <ClientReservation
                  isPrimaryClient={index === 0}
                  key={client.id}
                  index={index}
                  defaultOptions={defaultOptions}
                  clientOptions={clientOptions}
                  locationOptions={locationOptions}
                  doctorOptions={doctorOptions}
                  reservationSettingState={reservationSettingState}
                  setReservationSettingState={setReservationSettingState}
                  selectedClients={selectedClients}
                  setSelectedClients={setSelectedClients}
                  onPrimarySuggestionsUpdate={
                    index === 0 ? handlePrimarySuggestionsUpdate : undefined
                  }
                  openManualDialog={
                    index === 0
                      ? () => {
                          setReservationSettingState((state) => {
                            return {
                              ...state,
                              showManual: true,
                              prevState: state,
                            };
                          });
                        }
                      : undefined
                  }
                  clearManualSelection={
                    index === 0 ? clearManualSelection : undefined
                  }
                  doctor_required={defaults?.note?.doctor_required ?? false}
                />
              ))}
              <FormErrorList formErrors={errors} />
            </Grid>
          </Box>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close()} variant="outlined">
          {t("form.cancel")}
        </Button>
        <Button
          type="submit"
          disabled={submitDisabled}
          sx={{ padding: "0.7rem 2.4rem", gap: "0.6rem" }}
          variant="contained"
          form="reservationForm"
        >
          {loading && <ButtonLoader />}
          <FontAwesomeIcon icon={faCalendarDays} />
          {t("form.reserve")}
        </Button>
      </DialogActions>

      {showManual && (
        <ManualDateSelectionDialog
          clientState={clientState}
          selectedClients={selectedClients}
          setSelectedClients={setSelectedClients}
          reservationSettingState={reservationSettingState}
          setReservationSettingState={setReservationSettingState}
          doctorOptions={doctorOptions}
          close={() =>
            setReservationSettingState((state) => ({
              ...state,
              showManual: false,
            }))
          }
          cancel={() => {
            setReservationSettingState(prevState);
          }}
        />
      )}
    </Dialog>
  );
};
